import { useEffect, useState } from 'react';
import { Order } from '../types';
import { formatBRL, formatDateTimeBR } from '../utils/format';
import {
  CheckCircle2,
  FileDown,
  ArrowRight,
  ShieldCheck,
} from 'lucide-react';

interface OrderConfirmationPageProps {
  orderNumber: string;
  onNavigate: (path: string) => void;
}

export function OrderConfirmationPage({ orderNumber, onNavigate }: OrderConfirmationPageProps) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`/api/orders/${orderNumber}`)
      .then((res) => {
        if (!res.ok) throw new Error('Pedido não encontrado.');
        return res.json();
      })
      .then((data: any) => setOrder(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 space-y-6 animate-pulse">
        <div className="h-12 bg-surface-muted rounded-xs w-1/2 mx-auto" />
        <div className="h-64 bg-surface-muted rounded-xs" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
        <h2 className="text-2xl font-serif font-medium text-ink">Pedido não localizado</h2>
        <p className="text-xs text-ink-muted">{error || 'Verifique o número do pedido informado.'}</p>
        <button
          onClick={() => onNavigate('/')}
          className="bg-ink text-ink-inverted px-6 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
        >
          Voltar ao Início
        </button>
      </div>
    );
  }

  const isPaid = order.payment_status === 'paid';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-10">
      {/* Success Badge Banner */}
      <div className="bg-surface border border-border-subtle p-8 rounded-xs shadow-subtle text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-olive-light text-olive mx-auto flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8" />
        </div>

        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
            {isPaid ? 'Pedido Confirmado com Sucesso' : 'Pedido Registrado • Aguardando Pagamento'}
          </span>
          <h1 className="text-3xl font-serif font-normal text-ink">
            Obrigado pela sua compra no Aura Ateliê
          </h1>
          <p className="text-xs text-ink-muted">
            Número do Pedido: <strong className="font-mono text-ink text-sm">{order.order_number}</strong> &bull; Registrado em {formatDateTimeBR(order.created_at)}
          </p>
        </div>

        <div className="pt-4 flex flex-wrap justify-center gap-3 text-xs">
          <span className="px-3 py-1 bg-background-alt rounded-full border border-border-subtle">
            Status do Pedido: <strong className="capitalize text-ink">{order.status}</strong>
          </span>
          <span className="px-3 py-1 bg-background-alt rounded-full border border-border-subtle">
            Pagamento: <strong className="capitalize text-ink">{order.payment_status}</strong>
          </span>
          <span className="px-3 py-1 bg-background-alt rounded-full border border-border-subtle">
            Fulfillment: <strong className="capitalize text-ink">{order.fulfillment_status}</strong>
          </span>
        </div>
      </div>

      {/* Digital Products Download Card (If Paid) */}
      {isPaid && order.items && order.items.some((i) => i.product_type_snapshot === 'digital') && (
        <div className="bg-primary-subtle border border-primary/30 p-6 rounded-xs space-y-4">
          <div className="flex items-center gap-2">
            <FileDown className="w-5 h-5 text-primary" />
            <h3 className="font-serif text-lg font-medium text-ink">Seus Downloads Digitais Disponíveis</h3>
          </div>
          <p className="text-xs text-ink-muted leading-relaxed">
            Seu pagamento foi confirmado! Seus arquivos digitais com licença de uso estão liberados para download imediato através do nosso servidor seguro.
          </p>
          <div className="space-y-2 pt-2">
            {order.items
              .filter((i) => i.product_type_snapshot === 'digital')
              .map((item) => (
                <div key={item.id} className="bg-surface p-4 rounded-xs border border-border-subtle flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-ink">{item.product_title_snapshot}</p>
                    <p className="text-[11px] text-ink-muted">{item.variant_title_snapshot}</p>
                  </div>
                  <a
                    href={`/api/orders/${order.order_number}/download/file_${item.product_title_snapshot.toLowerCase().replace(/\s+/g, '_')}`}
                    className="bg-ink text-ink-inverted px-4 py-2 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors inline-flex items-center gap-1.5"
                    download
                  >
                    <FileDown className="w-3.5 h-3.5" /> Baixar Arquivo
                  </a>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Order Details & Summary Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Line Items */}
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4">
          <h3 className="font-serif text-lg font-medium text-ink border-b border-border-subtle pb-3">
            Peças do Pedido
          </h3>
          <div className="divide-y divide-border-subtle space-y-3">
            {order.items?.map((item) => (
              <div key={item.id} className="pt-3 first:pt-0 flex justify-between items-start text-xs">
                <div>
                  <p className="font-medium text-ink">{item.product_title_snapshot}</p>
                  <p className="text-ink-muted">{item.variant_title_snapshot}</p>
                  <p className="text-ink-muted font-mono text-[11px]">Qtd: {item.quantity} &bull; SKU: {item.sku_snapshot}</p>
                </div>
                <span className="font-mono text-ink font-medium">
                  {formatBRL(item.total_cents)}
                </span>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-border-subtle space-y-1.5 text-xs">
            <div className="flex justify-between text-ink-muted">
              <span>Subtotal</span>
              <span className="font-mono text-ink">{formatBRL(order.subtotal_cents)}</span>
            </div>
            <div className="flex justify-between text-ink-muted">
              <span>Frete</span>
              <span className="font-mono text-ink">{order.shipping_cents === 0 ? 'Grátis' : formatBRL(order.shipping_cents)}</span>
            </div>
            <div className="pt-2 border-t border-border-subtle flex justify-between font-medium text-sm text-ink">
              <span>Total Pago</span>
              <span className="font-mono text-base">{formatBRL(order.total_cents)}</span>
            </div>
          </div>
        </div>

        {/* Delivery / Fulfillment Details */}
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4">
          <h3 className="font-serif text-lg font-medium text-ink border-b border-border-subtle pb-3">
            Informações de Entrega & Rastreio
          </h3>

          {order.shipping_address ? (
            <div className="text-xs text-ink-muted space-y-1">
              <p className="font-medium text-ink">{order.shipping_address.recipient_name}</p>
              <p>{order.shipping_address.street}, {order.shipping_address.number} {order.shipping_address.complement || ''}</p>
              <p>{order.shipping_address.neighborhood} - {order.shipping_address.city}/{order.shipping_address.state}</p>
              <p className="font-mono">CEP: {order.shipping_address.postal_code}</p>
            </div>
          ) : (
            <p className="text-xs text-ink-muted">
              Este pedido contém apenas produtos digitais ou serviços que não exigem remessa física postal.
            </p>
          )}

          <div className="pt-4 border-t border-border-subtle space-y-2 text-xs">
            <div className="flex items-center gap-2 text-olive font-medium">
              <ShieldCheck className="w-4 h-4" />
              <span>Pedido Registrado de Forma Durável no D1</span>
            </div>
            <p className="text-ink-muted text-[11px] leading-relaxed">
              Você pode consultar os detalhes deste pedido e baixar notas fiscais a qualquer momento acessando sua conta.
            </p>
          </div>
        </div>
      </div>

      <div className="text-center pt-4">
        <button
          onClick={() => onNavigate('/catalogo')}
          className="bg-ink text-ink-inverted px-8 py-3.5 rounded-xs text-xs uppercase tracking-widest font-medium hover:bg-primary transition-colors inline-flex items-center gap-2"
        >
          Continuar Explorando o Ateliê
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
